import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft,
  Package2,
  Plus,
  Loader2,
  Pencil,
  Archive,
  Trash2,
  Box,
  Scroll,
  Tag,
  Wind,
  Printer,
  CircleDot,
} from "lucide-react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { NumericInput } from "../components/ui/numeric-input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const TYPE_OPTIONS = [
  { v: "barquette", l: "Barquette", icon: Box, color: "#1F4E3D" },
  { v: "film", l: "Film", icon: Scroll, color: "#3A6BC1" },
  { v: "gaz", l: "Gaz", icon: Wind, color: "#5B3A8C" },
  { v: "etiquette", l: "Étiquette", icon: Tag, color: "#8C6B2F" },
  { v: "consommable_imprimante", l: "Encre / cartouche", icon: Printer, color: "#A04E2C" },
  { v: "autre", l: "Autre", icon: CircleDot, color: "#575A57" },
];

const emptyForm = {
  name: "",
  type: "barquette",
  description: "",
  units_per_pack: 1000,
  pack_price_chf: "",
  roll_length_m: "",
  consumption_unit: "unité",
  current_stock: "",
  min_stock_alert: "",
  notes: "",
};

/**
 * Page admin : CRUD des consommables emballage (Phase 60).
 * Route : /parametres/emballages
 */
export default function PackagingConsumables() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/packaging-consumables?include_archived=${showArchived}`);
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (item) => {
    setEditing(item);
    setForm({
      name: item.name || "",
      type: item.type || "barquette",
      description: item.description || "",
      units_per_pack: item.units_per_pack ?? 1,
      pack_price_chf: item.pack_price_chf ?? "",
      roll_length_m: item.roll_length_m ?? "",
      consumption_unit: item.consumption_unit || "unité",
      current_stock: item.current_stock ?? "",
      min_stock_alert: item.min_stock_alert ?? "",
      notes: item.notes || "",
    });
    setDialogOpen(true);
  };

  const parseNum = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const cleaned = String(v).replace(",", ".").trim();
    const n = Number(cleaned);
    return isNaN(n) ? null : n;
  };

  const save = async () => {
    if (!form.name?.trim()) {
      toast.error("Le nom est obligatoire");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        type: form.type,
        description: form.description || "",
        units_per_pack: parseNum(form.units_per_pack) ?? 1,
        pack_price_chf: parseNum(form.pack_price_chf),
        roll_length_m: parseNum(form.roll_length_m),
        consumption_unit: form.consumption_unit || "unité",
        current_stock: parseNum(form.current_stock),
        min_stock_alert: parseNum(form.min_stock_alert),
        notes: form.notes || "",
      };
      if (editing) {
        await api.patch(`/packaging-consumables/${editing.id}`, payload);
        toast.success("Consommable mis à jour");
      } else {
        await api.post("/packaging-consumables", payload);
        toast.success("Consommable créé");
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : Array.isArray(detail) && detail[0]?.msg ? detail[0].msg : "Erreur";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const archive = async (item) => {
    if (!confirm(`Archiver "${item.name}" ? Les produits liés conserveront la référence mais ne pourront plus utiliser ce consommable pour de nouvelles liaisons.`)) return;
    try {
      await api.delete(`/packaging-consumables/${item.id}`);
      toast.success("Archivé");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const restore = async (item) => {
    try {
      await api.patch(`/packaging-consumables/${item.id}`, { archived: false });
      toast.success("Restauré");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const fmtUnitCost = (item) => {
    const pp = item.pack_price_chf;
    if (pp == null) return "—";
    if (item.type === "film" && item.roll_length_m && item.roll_length_m > 0) {
      return `${(pp / item.roll_length_m).toFixed(3)} CHF / m`;
    }
    if (item.units_per_pack && item.units_per_pack > 0) {
      return `${(pp / item.units_per_pack).toFixed(3)} CHF / unité`;
    }
    return "—";
  };

  return (
    <div className="space-y-5" data-testid="packaging-consumables-page">
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Package2 className="w-5 h-5 text-[#1F4E3D]" />
          <h2 className="font-heading text-xl font-semibold text-[#1A1C1A]">
            Consommables emballage
          </h2>
          <Badge variant="outline" className="font-mono text-[10px]">
            {items.length}
          </Badge>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/parametres"
            className="text-sm text-[#575A57] hover:text-[#1F4E3D] flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" /> Paramètres
          </Link>
          <label className="text-xs text-[#575A57] flex items-center gap-1">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              data-testid="show-archived-toggle"
            />
            Voir archivés
          </label>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-consumable-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouveau consommable
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl" data-testid="consumable-dialog">
              <DialogHeader>
                <DialogTitle>{editing ? "Modifier le consommable" : "Nouveau consommable"}</DialogTitle>
                <DialogDescription>
                  Indiquez le conditionnement d'achat et le prix. Le coût par unité est calculé automatiquement.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="mb-1.5 block">Nom *</Label>
                  <Input
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Barquette PP 178x153x5 (1 compartiment)"
                    data-testid="consumable-name"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1.5 block">Type *</Label>
                    <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v, consumption_unit: v === "film" ? "m" : "unité" })}>
                      <SelectTrigger data-testid="consumable-type">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TYPE_OPTIONS.map((o) => (
                          <SelectItem key={o.v} value={o.v}>
                            {o.l}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Unité de consommation</Label>
                    <Select value={form.consumption_unit} onValueChange={(v) => setForm({ ...form, consumption_unit: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unité">unité</SelectItem>
                        <SelectItem value="m">m (mètre)</SelectItem>
                        <SelectItem value="g">g (gramme)</SelectItem>
                        <SelectItem value="ml">ml</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="mb-1.5 block">Description / dimensions</Label>
                  <Textarea
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    rows={2}
                    placeholder="178x153x5mm, Polypropylène, transparente"
                    data-testid="consumable-description"
                  />
                </div>

                <div className="bg-[#FAFAF7] border border-[#E5E5E0] rounded p-3 space-y-3">
                  <div className="text-xs font-semibold text-[#1F4E3D]">Conditionnement et prix d'achat</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="mb-1.5 block">Prix du pack (CHF)</Label>
                      <NumericInput
                        value={form.pack_price_chf}
                        onChange={(e) => setForm({ ...form, pack_price_chf: e.target.value })}
                        placeholder="250"
                        className="font-mono"
                        data-testid="consumable-pack-price"
                      />
                    </div>
                    <div>
                      <Label className="mb-1.5 block">
                        {form.type === "film" ? "Longueur du rouleau (m)" : "Unités par pack"}
                      </Label>
                      {form.type === "film" ? (
                        <NumericInput
                          value={form.roll_length_m}
                          onChange={(e) => setForm({ ...form, roll_length_m: e.target.value })}
                          placeholder="500"
                          className="font-mono"
                          data-testid="consumable-roll-length"
                        />
                      ) : (
                        <NumericInput
                          value={form.units_per_pack}
                          onChange={(e) => setForm({ ...form, units_per_pack: e.target.value })}
                          placeholder="1000"
                          className="font-mono"
                          data-testid="consumable-units-per-pack"
                        />
                      )}
                    </div>
                  </div>
                  {form.pack_price_chf && (form.units_per_pack || form.roll_length_m) && (
                    <div className="text-xs text-[#1F4E3D] bg-white border border-[#A8C9B6] rounded p-2 font-mono">
                      Coût calculé :{" "}
                      {form.type === "film" && form.roll_length_m
                        ? `${(parseNum(form.pack_price_chf) / parseNum(form.roll_length_m)).toFixed(3)} CHF / m`
                        : `${(parseNum(form.pack_price_chf) / parseNum(form.units_per_pack)).toFixed(3)} CHF / unité`}
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-1.5 block">Stock actuel (packs)</Label>
                    <NumericInput
                      value={form.current_stock}
                      onChange={(e) => setForm({ ...form, current_stock: e.target.value })}
                      placeholder="3"
                      className="font-mono"
                    />
                  </div>
                  <div>
                    <Label className="mb-1.5 block">Alerte stock min (packs)</Label>
                    <NumericInput
                      value={form.min_stock_alert}
                      onChange={(e) => setForm({ ...form, min_stock_alert: e.target.value })}
                      placeholder="1"
                      className="font-mono"
                    />
                  </div>
                </div>

                <div>
                  <Label className="mb-1.5 block">Notes (fournisseur, référence…)</Label>
                  <Textarea
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    rows={2}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button onClick={save} disabled={saving} data-testid="save-consumable">
                  {saving && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                  {editing ? "Enregistrer" : "Créer"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {loading ? (
        <div className="py-12 text-center text-[#575A57]">
          <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
          Chargement…
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-lg p-12 text-center" data-testid="empty-state">
          <Package2 className="w-10 h-10 mx-auto text-[#A3A6A3] mb-3" />
          <p className="text-[#1A1C1A] font-medium mb-1">Aucun consommable</p>
          <p className="text-sm text-[#575A57] mb-4">
            Créez vos barquettes, films opercule et étiquettes pour calculer le prix de revient complet de vos produits.
          </p>
          <Button onClick={openCreate} className="bg-[#1F4E3D] hover:bg-[#173B2E]">
            <Plus className="w-4 h-4 mr-2" /> Créer le premier consommable
          </Button>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden" data-testid="consumables-table">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Nom</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Type</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Conditionnement</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Coût</TableHead>
                <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Stock</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => {
                const typeCfg = TYPE_OPTIONS.find((t) => t.v === item.type) || TYPE_OPTIONS[5];
                const Icon = typeCfg.icon;
                const isLowStock = item.min_stock_alert != null && item.current_stock != null && item.current_stock <= item.min_stock_alert;
                return (
                  <TableRow key={item.id} className={item.archived ? "opacity-50" : ""} data-testid={`row-${item.id}`}>
                    <TableCell>
                      <div className="font-medium text-[#1A1C1A]">{item.name}</div>
                      {item.description && (
                        <div className="text-xs text-[#A3A6A3] mt-0.5">{item.description}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5 text-xs" style={{ color: typeCfg.color }}>
                        <Icon className="w-3.5 h-3.5" /> {typeCfg.l}
                      </div>
                    </TableCell>
                    <TableCell className="text-xs font-mono text-[#575A57]">
                      {item.type === "film" && item.roll_length_m
                        ? `${item.roll_length_m} m / ${item.pack_price_chf ?? "?"} CHF`
                        : `${item.units_per_pack ?? 1} unités / ${item.pack_price_chf ?? "?"} CHF`}
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-[#1F4E3D] font-semibold">{fmtUnitCost(item)}</span>
                    </TableCell>
                    <TableCell>
                      {item.current_stock != null ? (
                        <div className={`text-xs font-mono ${isLowStock ? "text-[#C05C47]" : "text-[#575A57]"}`}>
                          {item.current_stock} packs
                          {isLowStock && <span className="ml-1">⚠️</span>}
                        </div>
                      ) : (
                        <span className="text-xs text-[#A3A6A3]">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEdit(item)}
                          data-testid={`edit-${item.id}`}
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </Button>
                        {item.archived ? (
                          <Button variant="ghost" size="icon" onClick={() => restore(item)} title="Restaurer">
                            <Archive className="w-3.5 h-3.5 text-[#1F4E3D]" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" onClick={() => archive(item)} title="Archiver">
                            <Trash2 className="w-3.5 h-3.5 text-[#C05C47]" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
